import type { LicenceResponse, ProfileResponse } from '@shared/schemas';

import {
  EMPTY_ENRICHMENT_STATS,
  type EnrichmentStats,
} from '../../_shared/domain/enrichment';
import {
  type FileImportData,
  type FileUploadState,
  initialFileUploadState,
} from '../../_shared/domain/file-import.model';
import { createInitialTree, type FilterTreeState } from '../../_shared/domain/filter-tree';
import {
  initialListImportState,
  type ListImportState,
} from '../../_shared/domain/list-import.model';
import type { AddListError, ListDraft, LoadStatus, SaveStatus } from '../domain/list-draft.model';
import { EMPTY_SELECTION, type RowSelection } from '../domain/row-selection';

/** The paginated profile table, filled by the fetch saga. */
export interface ProfileTableState {
  rows: ProfileResponse[];
  total: number;
  /** 0-indexed page (the saga converts to the 1-indexed server page). */
  page: number;
  pageSize: number;
  loading: boolean;
}

/**
 * Redux state for the full Ad List wizard. Beyond the draft + save lifecycle,
 * this now owns the entire profile-selection data layer that used to live in the
 * shared hooks (filters, pagination, duplicate count, enrichment buckets). Every
 * field is serialisable so the whole slice can be persisted / time-travelled.
 */
export interface AddListState {
  draft: ListDraft;
  step: number;
  validationErrors: Record<string, string>;
  saveStatus: SaveStatus;
  savePhase: string;
  error: AddListError | null;
  createdListId: string | null;

  // ── Selection (serialisable) ──
  excludeDuplicates: boolean;
  importedProfileIds: string[];
  importedListName: string;
  /** Embedded upload → map → analyze mini-flow; its output is `fileImportData`. */
  fileUpload: FileUploadState;
  fileImportData: FileImportData | null;
  /** The picked file's size, for its card ("48 KB"). */
  fileSize: number | null;
  /** Bytes sent while the file uploads; `null` once sent (the server then reads it). */
  fileProgress: { loaded: number; total: number } | null;
  enrichNew: boolean;
  enrichStale: boolean;

  // ── Filters (tree is the source of truth; serverFilters is a derived selector) ──
  filters: FilterTreeState;

  // ── Profile table (filled by the fetch saga) ──
  profiles: ProfileTableState;
  /** The rows ticked in the table (#595, board E3); starts over with every new filter. */
  rowSelection: RowSelection;

  // ── Async results ──
  duplicateCount: number;
  duplicateLoading: boolean;
  enrichment: EnrichmentStats;
  /** `failed` keeps the list from being created: its selected profiles come from the buckets. */
  enrichmentStatus: LoadStatus;
  nlSearching: boolean;

  // ── Licence dropdown (details step) ──
  licences: LicenceResponse[];
  licencesLoading: boolean;

  // ── Name suggestion (details step, #595) ──
  /** Names of the picked licence's lists; `null` until loaded (or without a licence). */
  licenceListNames: string[] | null;
  /** The last suggestion written into the name; the name follows it until edited. */
  nameSuggestion: string;

  // ── "Import from existing list" step ──
  listImport: ListImportState;
}

export const emptyDraft: ListDraft = {
  licenceId: '',
  licenceName: '',
  name: '',
  startDate: '',
  endDate: '',
  scope: '',
  clientScopeDescription: '',
  methods: [],
};

/** Default page size; matches the previous `useInfiniteApi(..., 25, ...)`. */
export const DEFAULT_PAGE_SIZE = 25;

export const initialState: AddListState = {
  draft: emptyDraft,
  step: 0,
  validationErrors: {},
  saveStatus: 'idle',
  savePhase: '',
  error: null,
  createdListId: null,

  excludeDuplicates: true,
  importedProfileIds: [],
  importedListName: '',
  fileUpload: initialFileUploadState,
  fileImportData: null,
  fileSize: null,
  fileProgress: null,
  enrichNew: false,
  enrichStale: false,

  filters: createInitialTree(),

  profiles: {
    rows: [],
    total: 0,
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    loading: false,
  },
  rowSelection: EMPTY_SELECTION,

  duplicateCount: 0,
  duplicateLoading: false,
  enrichment: EMPTY_ENRICHMENT_STATS,
  enrichmentStatus: 'idle',
  nlSearching: false,

  licences: [],
  licencesLoading: false,

  licenceListNames: null,
  nameSuggestion: '',

  listImport: initialListImportState,
};
